// src/components/CalendarView.js

import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Box, Typography } from "@mui/material";
import { fetchEntries } from "../entriesApi";
import { fetchEntryDatesWithBlocks } from "../albumApi";
import { useAlbum } from "../context/AlbumContext";
import { startOfLocalDay } from "../utils/dateUtils";

const CalendarView = ({ selectedDate, setSelectedDate, selectedDates, setSelectedDates, multiSelect }) => {
  const [markedDates, setMarkedDates] = useState([]);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(selectedDate || new Date()));
  const { currentAlbumId } = useAlbum();

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (currentAlbumId) {
        const rangeStart = startOfMonth(visibleMonth);
        const rangeEnd = addMonths(rangeStart, 2);
        const entryDates = await fetchEntryDatesWithBlocks(currentAlbumId, rangeStart, rangeEnd);
        if (!cancelled) {
          setMarkedDates(entryDates.map(startOfLocalDay));
        }
        return;
      }

      const entries = await fetchEntries();
      if (!cancelled) {
        setMarkedDates(entries.map((entry) => new Date(entry.createdAt.seconds * 1000)));
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [currentAlbumId, visibleMonth]);

  useEffect(() => {
    if (!multiSelect && selectedDate) {
      setVisibleMonth(startOfMonth(selectedDate));
    }
  }, [multiSelect, selectedDate]);

  const handleDateSelect = (dates) => {
    if (multiSelect) {
      // For multi-select, dates will be an array of two dates: [startDate, endDate]
      const [start, end] = dates;
      if (start && end) {
        // Generate an array of dates between start and end
        const dateRange = [];
        let currentDate = new Date(start);
        while (currentDate <= end) {
          dateRange.push(currentDate.toISOString().split('T')[0]);
          currentDate.setDate(currentDate.getDate() + 1);
        }
        setSelectedDates(dateRange);
      } else if (start) {
        // If only start is selected, add just that date
        setSelectedDates([start.toISOString().split('T')[0]]);
      } else {
        // If no dates are selected (user unselected all), clear the array
        setSelectedDates([]);
      }
    } else {
      // For single select, dates will be a single date
      setSelectedDate(dates);
    }
  };

  const highlightWithRanges = [
    {
      "react-datepicker__day--highlighted-custom-1": markedDates,
    },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" component="div">
        Calendar
      </Typography>
      <DatePicker
        selected={multiSelect ? null : selectedDate}
        onChange={handleDateSelect}
        highlightDates={highlightWithRanges}
        inline
        monthsShown={2}
        onMonthChange={(date) => setVisibleMonth(startOfMonth(date))}
        onYearChange={(date) => setVisibleMonth(startOfMonth(date))}
        selectsRange={multiSelect}
        startDate={multiSelect ? (selectedDates.length > 0 ? new Date(selectedDates[0]) : null) : null}
        endDate={multiSelect ? (selectedDates.length > 1 ? new Date(selectedDates[selectedDates.length - 1]) : null) : null}
        renderDayContents={(day, date) => {
          return (
            <div
              style={{
                backgroundColor: multiSelect && selectedDates.includes(date.toISOString().split('T')[0]) ? 'lightblue' : 'transparent',
                borderRadius: '50%',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {day}
            </div>
          );
        }}
      />
    </Box>
  );
};

function startOfMonth(date) {
  const value = startOfLocalDay(date);
  return new Date(value.getFullYear(), value.getMonth(), 1);
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

export default CalendarView;
